import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CITIES, EMAIL, PHONE_DISPLAY, PHONE_HREF } from '../data/site';
import { track, EVENTS } from '../utils/analytics';
import type { NavItem } from './Header';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  nav: readonly NavItem[];
  isActive: (to: string) => boolean;
}

const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * The mobile off-canvas drawer, left-anchored per the 2026-09-24 spec (§14
 * overrides the general "from the right" instruction): the panel Keanu liked
 * from the old SideMenu, restyled to the v3 light tokens and the current nav.
 */
const Drawer: React.FC<DrawerProps> = ({ open, onClose, nav, isActive }) => {
  const reduced = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    lastFocused.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const raf = requestAnimationFrame(() => closeRef.current?.focus());

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      lastFocused.current?.focus();
    };
  }, [open, onClose]);

  const cta = (
    <Link
      to="/contact"
      className="button button--primary button--wide drawer__cta"
      onClick={() => {
        track(EVENTS.ctaClicked, { location: 'drawer', label: 'Get a free audit' });
        onClose();
      }}
    >
      Get a free audit
    </Link>
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="drawer__scrim"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.24 }}
            aria-hidden="true"
          />
          <motion.div
            id="site-drawer"
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            ref={panelRef}
            initial={reduced ? { opacity: 0 } : { x: '-100%' }}
            animate={reduced ? { opacity: 1 } : { x: 0 }}
            exit={reduced ? { opacity: 0 } : { x: '-100%' }}
            transition={{ duration: reduced ? 0.15 : 0.32, ease: EASE }}
          >
            <div className="drawer__head">
              <Link to="/" className="wordmark wordmark--lockup" onClick={onClose}>
                <img src="/brand/logo-full-flat.png" alt="JK Automations" width={73} height={40} />
              </Link>
              <button
                type="button"
                className="drawer__close"
                onClick={onClose}
                aria-label="Close menu"
                ref={closeRef}
              >
                <span aria-hidden="true">Close</span>
              </button>
            </div>

            <nav className="drawer__nav" aria-label="Main">
              {nav.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={reduced ? undefined : { opacity: 0, x: -24 }}
                  animate={reduced ? undefined : { opacity: 1, x: 0 }}
                  transition={{ duration: 0.24, ease: EASE, delay: reduced ? 0 : 0.06 + i * 0.04 }}
                >
                  <Link
                    to={item.to}
                    className={isActive(item.to) ? 'is-active' : undefined}
                    onClick={onClose}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <motion.div
              initial={reduced ? undefined : { opacity: 0, x: -24 }}
              animate={reduced ? undefined : { opacity: 1, x: 0 }}
              transition={{ duration: 0.24, ease: EASE, delay: reduced ? 0 : 0.06 + nav.length * 0.04 }}
            >
              {cta}
            </motion.div>

            <div className="drawer__contact">
              <a href={`tel:${PHONE_HREF}`} onClick={() => track(EVENTS.phoneClicked, { location: 'drawer' })}>
                {PHONE_DISPLAY}
              </a>
              <a href={`mailto:${EMAIL}`} onClick={() => track(EVENTS.emailClicked, { location: 'drawer' })}>
                {EMAIL}
              </a>
            </div>

            <p className="drawer__foot">{CITIES}</p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Drawer;
